import { useQuery } from '@tanstack/react-query';
import { AreaResponse, getAreaPresetTypeLabel } from '../../lib/areas';
import type { CurfewConfig } from '../../lib/weekly-availability';
import { presetsApi, PresetResponse } from '../../lib/presets';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PresetsList from '../presets/PresetsList';

interface AreaWithPresetsProps {
  area: AreaResponse;
  /** Site lighting curfew (for comparing preset rows to “full non-curfew”). */
  siteCurfew?: CurfewConfig;
  canManage: boolean;
  onEditArea: (area: AreaResponse) => void;
  onDeleteArea: (areaId: string) => void;
  onCreatePreset: (areaId: string) => void;
  onEditPreset: (preset: PresetResponse) => void;
  onDeletePreset?: (presetId: string, areaId: string) => void;
}

export default function AreaWithPresets({
  area,
  siteCurfew,
  canManage,
  onEditArea,
  onDeleteArea,
  onCreatePreset,
  onEditPreset,
  onDeletePreset,
}: AreaWithPresetsProps) {
  const { data: presets, isLoading: presetsLoading } = useQuery({
    queryKey: ['presets', area.id],
    queryFn: () => presetsApi.getByArea(area.id),
    enabled: !!area.id,
  });

  function handleDeletePreset(presetId: string) {
    onDeletePreset?.(presetId, area.id);
  }

  return (
    <Card className="bg-muted/50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h5 className="font-semibold text-foreground">{area.name}</h5>
              <Badge
                variant={
                  area.type === 'user'
                    ? 'default'
                    : area.type === 'light'
                      ? 'secondary'
                      : 'outline'
                }
                className="text-xs"
              >
                {getAreaPresetTypeLabel(area.type)}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
              {area.address !== null && area.address !== undefined && (
                <p>Address: {area.address}</p>
              )}
              {area.type === 'light' && (area.curfewStart || area.curfewEnd) && (
                <p>
                  Curfew: {area.curfewStart ?? '—'} – {area.curfewEnd ?? '—'}
                </p>
              )}
            </div>
          </div>
          {canManage && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEditArea(area)}
                title="Edit area"
                className="h-6 px-2"
              >
                <Pencil className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDeleteArea(area.id)}
                title="Delete area"
                className="h-6 px-2 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </div>

        {/* Presets Section within Area */}
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex justify-between items-center mb-2">
            <h6 className="text-sm font-medium text-foreground">{area.type === 'user' ? 'Groups' : 'Presets'}</h6>
            {canManage && (
              <Button
                size="sm"
                onClick={() => onCreatePreset(area.id)}
                className="h-6 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                {area.type === 'user' ? 'Add Group' : 'Add Preset'}
              </Button>
            )}
          </div>
          {presetsLoading ? (
            <p className="text-xs text-muted-foreground">Loading presets...</p>
          ) : presets && presets.length > 0 ? (
            <PresetsList
              areaType={area.type}
              areaCurfew={{ start: area.curfewStart, end: area.curfewEnd }}
              siteCurfew={siteCurfew}
              presets={presets}
              onEdit={onEditPreset}
              onDelete={onDeletePreset ? handleDeletePreset : undefined}
            />
          ) : (
            <div className="text-center py-2">
              <p className="text-xs text-muted-foreground mb-1">{area.type === 'user' ? 'No groups for this area' : 'No presets for this area'}</p>
              {canManage && (
                <Button
                  size="sm"
                  onClick={() => onCreatePreset(area.id)}
                  className="h-6 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {area.type === 'user' ? 'Add First Group' : 'Add First Preset'}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
